// Auth state lives here.
//
// Per Step 3.5 lock:
//   - Access JWT is short-lived (15 min) and lives in memory ONLY (NOT
//     localStorage — XSS would lift it). The httpOnly merchd_refresh
//     cookie carries the rotating 30-day refresh token and is what
//     survives a page reload.
//   - On mount, we try /auth/refresh — if the cookie is valid, we get
//     a fresh access JWT back without prompting the user. If not, we
//     stay logged-out and the route guard sends them to /login.
//   - api.ts auto-calls refreshToken() on 401 once + retries. Only a
//     definitive 401 from /auth/refresh signs out; a deploy or blip is
//     retried and never ends the session (lib/session-refresh.ts, 2026-10-22).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError, bindAuth } from "@/lib/api";
import { makeSessionRefresher, type RefreshOutcome } from "@/lib/session-refresh";

export interface SessionUser {
  sub: string;
  email: string;
  name: string;
  roles: string[];
  /** This session passed two-step sign-in. */
  mfa?: boolean;
}

/** Password was right; two-step sign-in still owed (2026-10-21). The token
 *  opens only the /auth/mfa/* endpoints and lasts 10 minutes. */
export interface TwoStepChallenge {
  token: string;
  /** true = set up the authenticator app first; false = enter a code. */
  setup: boolean;
  email: string;
}

export type LoginOutcome =
  | { kind: "signed_in" }
  | { kind: "two_step"; challenge: TwoStepChallenge };

/** What /auth/login and /auth/set-password answer when two-step is owed. */
export interface TwoStepRequiredBody {
  mfa_required: true;
  mfa_token: string;
  setup: boolean;
  email: string;
}

export function twoStepFromBody(body: unknown): TwoStepChallenge | null {
  const b = body as Partial<TwoStepRequiredBody> | null;
  if (!b || b.mfa_required !== true || typeof b.mfa_token !== "string") return null;
  return { token: b.mfa_token, setup: b.setup === true, email: b.email ?? "" };
}

interface AuthState {
  user: SessionUser | null;
  /** Page load couldn't reach the server (deploy / outage) and keeps
   *  retrying — RequireAuth shows "Can't reach … — retrying…", never /login. */
  unreachable: boolean;
  /** The Retry button: try again now instead of after the current wait. */
  retryNow: () => void;
  /** Gave up retrying (MAX_UNREACHABLE_CYCLES rounds, or the server said
   *  something retrying won't fix): show `unreachableMessage` + "Sign in
   *  again" instead of retrying forever. */
  unreachableGaveUp: boolean;
  unreachableMessage: string | null;
  /** True until we've finished the initial /auth/refresh attempt. */
  loading: boolean;
  /** Signs in, or reports that a two-step code (or setup) is still owed. */
  login: (email: string, password: string) => Promise<LoginOutcome>;
  /**
   * Adopt a session the server already minted — used by the invite
   * set-password flow, where POST /auth/set-password returns the access
   * token + user and sets the refresh cookie in one shot (auto-login). We
   * store exactly what login() stores, without duplicating the fetch.
   */
  adoptSession: (accessToken: string, sessionUser: SessionUser) => void;
  logout: () => Promise<void>;
  /**
   * Patch the in-memory session user. Used by the Settings page so a
   * profile name edit reflects in the header immediately (the next token
   * refresh re-reads it from the DB, so this just avoids the lag).
   */
  patchUser: (partial: Partial<SessionUser>) => void;
}

const AuthContext = createContext<AuthState | null>(null);

/** Rounds of renewal retries on page load before offering "Sign in again". */
const MAX_UNREACHABLE_CYCLES = 3;

/** One renewer per page: every caller shares the same in-flight renewal. */
const renewSession = makeSessionRefresher<SessionUser>({ url: "/auth/refresh" });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreachable, setUnreachable] = useState(false);
  const [unreachableGaveUp, setUnreachableGaveUp] = useState(false);
  const [unreachableMessage, setUnreachableMessage] = useState<string | null>(null);
  const accessTokenRef = useRef<string | null>(null);
  const navigate = useNavigate();

  // A fresh sign-in or sign-out ends any "can't reach" state from page load.
  const clearUnreachable = useCallback(() => {
    setUnreachable(false);
    setUnreachableGaveUp(false);
    setUnreachableMessage(null);
  }, []);

  const handleLogout = useCallback(() => {
    clearUnreachable();
    accessTokenRef.current = null;
    setUser(null);
    navigate("/login");
  }, [navigate, clearUnreachable]);

  // Renew from the refresh cookie: single flight, retried through deploys,
  // "signed out" only on a definitive 401 (lib/session-refresh.ts).
  const refresh = useCallback(async (): Promise<RefreshOutcome<SessionUser>> => {
    const outcome = await renewSession();
    if (outcome.kind === "ok") accessTokenRef.current = outcome.accessToken;
    return outcome;
  }, []);

  // Bind the api wrapper to our token state. Re-binds when handleLogout
  // changes (it depends on `navigate` which is stable, so this fires once).
  useEffect(() => {
    bindAuth({
      getAccessToken: () => accessTokenRef.current,
      refreshToken: refresh,
      onLogout: handleLogout,
    });
  }, [refresh, handleLogout]);

  // On first mount: try to silently refresh + load /auth/me. If anything
  // fails, stay logged-out (the route guard will push to /login).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Keep trying until the server gives a clear answer. A deploy or an
      // outage shows "Can't reach … — retrying…" (RequireAuth) instead of
      // the sign-in page; the cookie is kept throughout.
      let outcome = await refresh();
      let cycles = 0;
      while (!cancelled && outcome.kind === "unavailable") {
        cycles++;
        setUnreachable(true);
        setUnreachableMessage(outcome.message ?? null);
        // A way out: after a few rounds (~1 min each), or at once when the
        // server says something retrying won't fix, offer "Sign in again".
        if (outcome.definitive || cycles >= MAX_UNREACHABLE_CYCLES) {
          setUnreachableGaveUp(true);
          setLoading(false);
          return;
        }
        outcome = await refresh();
      }
      if (cancelled) return;
      setUnreachable(false);
      if (outcome.kind !== "ok") {
        setLoading(false); // definitively signed out → the sign-in page
        return;
      }
      try {
        // The renewal already says who this is; /me only as a fallback.
        const sessionUser =
          outcome.user ?? (await api<{ user: SessionUser }>("/auth/me")).user;
        if (!cancelled) setUser(sessionUser);
      } catch {
        // Refresh succeeded but /auth/me failed — token is bad somehow.
        accessTokenRef.current = null;
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginOutcome> => {
      const body = await api<
        { access_token: string; user: SessionUser } | TwoStepRequiredBody
      >("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
        skipAuthRefresh: true,
        skipAuth: true,
      });
      // Two-step sign-in: no session yet — the sign-in page asks for the code.
      const challenge = twoStepFromBody(body);
      if (challenge) return { kind: "two_step", challenge };
      const session = body as { access_token: string; user: SessionUser };
      clearUnreachable();
      accessTokenRef.current = session.access_token;
      setUser(session.user);
      return { kind: "signed_in" };
    },
    [],
  );

  const adoptSession = useCallback(
    (accessToken: string, sessionUser: SessionUser) => {
      clearUnreachable();
      accessTokenRef.current = accessToken;
      setUser(sessionUser);
    },
    [],
  );

  const patchUser = useCallback((partial: Partial<SessionUser>) => {
    setUser((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  const logout = useCallback(async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch (err) {
      // Soft-fail on the server call; we're tearing down state regardless
      if (err instanceof ApiError && err.status === 401) {
        // already gone
      } else {
        console.warn("[auth/logout] server call failed", err);
      }
    } finally {
      handleLogout();
    }
  }, [handleLogout]);

  return (
    <AuthContext.Provider
      value={{ user, loading, unreachable, unreachableGaveUp, unreachableMessage, retryNow: renewSession.retryNow, login, adoptSession, logout, patchUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
